import { Box, Stack, Typography } from "@mui/material";
import Tabs from './tabs';
import TemplatesCreation from "./templatesCreation";
import { useSelector } from "react-redux";
import TemplateCreationFlow from "./templateCreationFlow";
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import TranslateIcon from '@mui/icons-material/Translate';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import languages from "../../../components/languages/languages";

const BLUE = "#2563eb";
const GREEN = "#198754";

const STEPS = ["Template Details", "Template Flow"];

// ------------------- Top step bar: numbered pills joined by a line -------------------
function StepBar({ activeStep }) {
    return (
        <Stack
            direction="row"
            alignItems="center"
            sx={{
                p: { xs: 1, sm: 1.25 },
                borderRadius: 4,
                bgcolor: "#f8fafc",
                border: "1px solid #e9edf2",
                overflowX: "auto",
                "&::-webkit-scrollbar": { display: "none" },
            }}
        >
            {STEPS.map((label, i) => {
                const done = i < activeStep;
                const current = i === activeStep;
                return (
                    <Stack key={label} direction="row" alignItems="center" sx={{ flex: i < STEPS.length - 1 ? 1 : "0 0 auto", minWidth: 0 }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{
                                flexShrink: 0,
                                pl: 0.5,
                                pr: 1.75,
                                py: 0.5,
                                borderRadius: 5,
                                transition: "all .2s ease",
                                bgcolor: current ? BLUE : done ? `${GREEN}14` : "transparent",
                                boxShadow: current ? "0 6px 14px -6px rgba(37,99,235,0.7)" : "none",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 13,
                                    fontWeight: 800,
                                    color: current ? BLUE : "#fff",
                                    bgcolor: current ? "#fff" : done ? GREEN : "#cbd5e1",
                                }}
                            >
                                {done ? <CheckRoundedIcon sx={{ fontSize: 17 }} /> : i + 1}
                            </Box>
                            <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                    fontSize: { xs: 13, sm: 14 },
                                    fontWeight: current ? 800 : 700,
                                    color: current ? "#fff" : done ? GREEN : "#94a3b8",
                                }}
                            >
                                {label}
                            </Typography>
                        </Stack>
                        {i < STEPS.length - 1 && (
                            <Box sx={{ flex: 1, minWidth: 16, height: 2, mx: 1, borderRadius: 1, bgcolor: done ? GREEN : "#e2e8f0" }} />
                        )}
                    </Stack>
                );
            })}
        </Stack>
    );
}

// ------------------- Summary cards (display only) -------------------
function SummaryChip({ icon, label, value, color }) {
    const filled = Boolean(value);
    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            title={value || ""}
            sx={{
                position: "relative",
                minWidth: 0,
                px: 1.5,
                py: 1.1,
                borderRadius: 3,
                bgcolor: "#fff",
                border: `1px ${filled ? "solid" : "dashed"} ${filled ? "#e5e7eb" : "#cbd5e1"}`,
                borderLeft: `3px solid ${filled ? color : "#e2e8f0"}`,
                boxShadow: filled ? "0 1px 3px rgba(16,24,40,0.06)" : "none",
            }}
        >
            <Box
                sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: filled ? color : "#cbd5e1",
                    bgcolor: filled ? `${color}14` : "#f8fafc",
                    "& svg": { fontSize: 19 },
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" sx={{ display: "block", lineHeight: 1.2, color: "#94a3b8", fontWeight: 700, fontSize: 10.5, letterSpacing: 0.6, textTransform: "uppercase" }}>
                    {label}
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontSize: 14, fontWeight: filled ? 700 : 500, color: filled ? "#0f172a" : "#cbd5e1", lineHeight: 1.4 }}>
                    {value || "Not set yet"}
                </Typography>
            </Box>
            {filled && (
                <Box sx={{ position: "absolute", top: 6, right: 6, width: 16, height: 16, borderRadius: "50%", bgcolor: GREEN, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CheckRoundedIcon sx={{ fontSize: 12 }} />
                </Box>
            )}
        </Stack>
    );
}

function SummaryStrip() {
    const { templateNameData } = useSelector((state) => state.templates);
    const d = templateNameData?.[0] || {};
    const languageLabel = d.language ? languages.find((l) => l.value === d.language)?.label || d.language : "";

    return (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
            <SummaryChip icon={<DescriptionOutlinedIcon />} label="Name" value={d.templateName} color={BLUE} />
            <SummaryChip icon={<CategoryOutlinedIcon />} label="Category" value={d.categoryName} color="#7c3aed" />
            <SummaryChip icon={<TranslateIcon />} label="Language" value={languageLabel} color="#d97706" />
        </Box>
    );
}

// ------------------- Main Component -------------------
export default function TemplateSteper() {
    const { activeStep } = useSelector((state) => state.templates);

    return (
        <div>
            <Tabs>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Box>
                        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                            <Typography variant="caption" fontWeight={700} sx={{ color: BLUE }}>
                                Step {Math.min(activeStep + 1, STEPS.length)} of {STEPS.length}
                            </Typography>
                        </Stack>
                        <StepBar activeStep={activeStep} />
                    </Box>

                    {/* Details chosen in step 1 (shown once moved on) */}
                    {activeStep > 0 && <SummaryStrip />}

                    <Box>
                        {
                            activeStep === 0 ? (
                                <TemplatesCreation />
                            ) : activeStep === 1 ? (
                                <TemplateCreationFlow />
                            ) : null
                        }
                    </Box>
                </Stack>
            </Tabs>
        </div>
    );
}
